import React, { useState, useEffect } from 'react';
import { db } from '../../../config/firebase';
import { ref, get } from 'firebase/database';
import { formatCurrency } from '../../../utils/formatCurrency';
import { useBranches } from '../../../hooks/useBranches';
import { resolveBranchRef } from '../../../utils/branchResolver';

const CourseDetails = ({ formData, updateFormData, handleSave, isEditing }) => {
  const { byId: branchesById, bySlug: branchesBySlug } = useBranches();
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);

  useEffect(() => {
    const loadCourseDetails = async () => {
      try {
        setLoading(true); // Set loading to true at start
        // Load courses with their batches
        const coursesSnapshot = await get(ref(db, 'courses'));
        const coursesData = coursesSnapshot.val() || {};

        const coursesWithBatches = Object.entries(coursesData).map(
          ([id, course]) => ({
            id,
            ...course,
            batches: course.batches ? Object.values(course.batches) : [],
          }),
        );

        setAvailableCourses(coursesWithBatches);

        // If editing, load student's enrolled courses with batch details
        if (formData.joinedCourses?.length) {
          const enrolledCourses = formData.joinedCourses.map((enrollment) => {
            const course = coursesData[enrollment.courseId];
            const batch = course?.batches?.find(
              (b) => b.batchId === enrollment.batchId,
            );
            return {
              ...enrollment,
              courseName: course?.name,
              batchDetails: batch,
            };
          });
          setSelectedCourses(enrolledCourses);
        }
        setError(null);
      } catch (error) {
        console.error('Error loading course details:', error);
        setError('Failed to load course details');
      } finally {
        setLoading(false); // Set loading to false whether success or error
      }
    };

    loadCourseDetails();
  }, [formData.joinedCourses]);

  const handleCourseChange = (courseId) => {
    updateFormData({
      joinedCourses: [
        { courseId, batchId: formData.joinedCourses[0]?.batchId || '' },
      ],
    });
  };

  const handleBatchChange = (e) => {
    updateFormData({
      joinedCourses: [
        { ...formData.joinedCourses[0], batchId: e.target.value },
      ],
    });
  };

  const formatBatchTiming = (startDate) => {
    if (!startDate) return 'N/A';
    const { date, month, year } = startDate;
    return `${date}/${month}/${year}`;
  };

  const removeSelectedCourse = (index) => {
    const updatedCourses = selectedCourses.filter((_, i) => i !== index);
    setSelectedCourses(updatedCourses);

    // Update the parent form data
    updateFormData({
      joinedCourses: updatedCourses.map((course) => ({
        courseId: course.courseId,
        batchId: course.batchId,
        enrollmentDate: course.enrollmentDate,
        status: course.status,
      })),
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4 bg-red-50 rounded-md">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Course Details</h2>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Course
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableCourses.map((course) => (
              <div
                key={course.id}
                className={`flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer ${
                  (formData.joinedCourses || []).some(
                    (c) => c.courseId === course.id,
                  )
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200'
                }`}
                onClick={() => handleCourseChange(course.id)}
              >
                <input
                  type="checkbox"
                  id={course.id}
                  checked={(formData.joinedCourses || []).some(
                    (c) => c.courseId === course.id,
                  )}
                  onChange={() => handleCourseChange(course.id)}
                  className="mt-1"
                />
                <label htmlFor={course.id} className="cursor-pointer flex-1">
                  <div className="font-medium">{course.name}</div>
                  <div className="text-sm text-gray-500">
                    Duration: {course.curriculum?.duration?.months || 'N/A'}{' '}
                    months
                  </div>
                  <div className="text-sm text-gray-500">
                    Fee:{' '}
                    {course.fees?.fullCourseFee
                      ? formatCurrency(course.fees.fullCourseFee)
                      : 'N/A'}
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>

        {formData.joinedCourses.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Batch
            </label>
            <select
              value={formData.joinedCourses[0]?.batchId || ''}
              onChange={handleBatchChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a batch</option>
              {availableCourses
                .find((c) => c.id === formData.joinedCourses[0]?.courseId)
                ?.batches.map((batch, index) => {
                  const branchName =
                    resolveBranchRef(batch, {
                      byId: branchesById,
                      bySlug: branchesBySlug,
                    })?.name || 'Branch not set';
                  return (
                    <option key={index} value={batch.id}>
                      {branchName} ({formatBatchTiming(batch.startDate)})
                    </option>
                  );
                })}
            </select>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="flex justify-end mt-4">
          <span onClick={handleSave} className="text-blue-600 cursor-pointer">
            Save
          </span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {selectedCourses.map((course, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-2 border rounded"
        >
          <div>
            <p className="font-medium">{course.courseName}</p>
            <p className="text-sm text-gray-600">Batch ID: {course.batchId}</p>
          </div>
          <button
            onClick={() => removeSelectedCourse(index)}
            className="text-red-600 hover:text-red-800"
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
};

export default CourseDetails;
